<template>
  <div>
    <div class="d-flex flex-column flex-sm-row justify-space-between">
      <div>
        {{ teacherSchedule.teacherFirstName }}
        {{ teacherSchedule.teacherLastName }}
        <span v-if="!globalCenter || !globalCenter.id">
          @ {{ getCenterName(teacherSchedule.centerId) }}
        </span>
      </div>
      <span>
        {{ teacherSchedule.startTime | formatDate }}
        {{ teacherSchedule.startTime | formatTime }} -
        {{ teacherSchedule.endTime | formatTime }}
      </span>
    </div>
    <v-divider />

    <div class="d-flex justify-space-between my-2">
      <v-alert
        border="left"
        colored-border
        dense
        type="info"
        class="my-2 flex-grow-1"
      >
        {{
          recurringEdit
            ? "Editing recurring schedule"
            : "Editing an instance of a recurring schedule"
        }}
      </v-alert>
      <a class="my-auto" text @click="toggleEditMode">
        {{ recurringEdit ? "Edit Instance" : "Edit Recurring" }}
      </a>
    </div>
    <div
      v-if="isRecurring"
      class="d-flex flex-column flex-sm-row justify-space-between my-2"
    >
      <div class="text-caption">
        Weekly on {{ teacherSchedule.recurringDays.join(", ") }}
      </div>
      <div class="text-caption">
        Since {{ teacherSchedule.recurringStartDate | formatDate }}
        {{
          teacherSchedule.recurringEndDate
            ? ` until ${$options.filters.formatDate(
                teacherSchedule.recurringEndDate
              )}`
            : " (no end date)"
        }}
      </div>
    </div>

    <v-divider />

    <v-form class="mt-8" ref="form" v-model="valid">
      <div class="d-flex justify-space-between">
        <v-autocomplete
          :items="centerClassrooms"
          v-model="selectedClassroom"
          item-value="id"
          item-text="roomNumber"
          label="Assigned Classroom"
          dense
          clearable
        />
      </div>
      <date-picker
        :label="recurringEdit ? 'Start Date' : 'Date'"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="selectedDate"
        :outlined="false"
        clearable
        :rules="[rules.required]"
        required
      />
      <v-text-field
        v-model="internalStartTime"
        label="Start Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-text-field
        v-model="internalEndTime"
        label="End Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-textarea v-model="notes" label="Notes" :rows="1" auto-grow />
      <div v-if="recurringEdit">
        <date-picker
          label="End Date (Optional)"
          :defaultNowOnNull="false"
          :show-hint="false"
          :show-icon="false"
          v-model="endDate"
          :outlined="false"
          clearable
        />
        <div class="d-flex justify-space-around">
          <v-checkbox
            v-for="(day, index) in daysOfWeek"
            :key="index"
            v-model="day.selected"
            :label="day.label"
          />
        </div>
      </div>

      <v-card-actions>
        <!-- <v-tooltip v-if="isAttendanceDataLoaded && isDeletable" bottom> -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="error"
              icon
              v-bind="attrs"
              v-on="on"
              @click="onDeleteScheduleClick"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>
            Delete {{ recurringEdit ? "Recurring" : "This Instance" }}
          </span>
        </v-tooltip>

        <v-btn
          class="mx-auto"
          color="green darken-1"
          text
          :loading="isLoading"
          @click="updateSchedule"
        >
          Update Schedule
        </v-btn>
        <v-btn color="blue darken-1" text @click="onClose"> Close </v-btn>
      </v-card-actions>
    </v-form>
  </div>
</template>

<script>
import dialogMixin from "./dialogMixin.js";
import { mapGetters, mapActions } from "vuex";
import { updateTeacherScheduleOccurrence } from "@/services/schedulingService.js";
import moment from "moment";
import { EventBus } from "@/EventBus.js";
import { openOverlapScheduleErrors } from "@/components/schedule/eventHandler.js";

export default {
  mixins: [dialogMixin],
  props: {
    teacherSchedule: {
      type: Object,
      required: true,
    },
    classrooms: {
      type: Array,
      required: false,
    },
    teachers: {
      type: Array,
      required: false,
    },
    allItems: {
      type: Array,
      required: false,
    },
  },
  data: () => ({
    selectedClassroom: null,
    isRecurring: false,
    recurringEdit: false,
    selectedDate: null,
    endDate: null,
    internalStartTime: null,
    internalEndTime: null,
    notes: null,
    isAttendanceDataLoaded: false,
    lastAttendedDate: null,
    isLoading: false,
    rules: {
      required: (value) => !!value || "Required.",
    },
    daysOfWeek: [
      { label: "Sun", selected: false },
      { label: "Mon", selected: false },
      { label: "Tue", selected: false },
      { label: "Wed", selected: false },
      { label: "Thu", selected: false },
      { label: "Fri", selected: false },
      { label: "Sat", selected: false },
    ],
  }),
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),
    isDeletable() {
      // Only compute isDeletable if attendance data is loaded
      if (!this.isAttendanceDataLoaded) {
        return false;
      }

      if (this.lastAttendedDate && this.teacherSchedule.recurringEndDate) {
        // Parse the dates with moment.js for reliable comparison
        const scheduleEndDate = moment(this.teacherSchedule.recurringEndDate);
        const lastAttended = moment(this.lastAttendedDate);

        // Compare the dates
        if (scheduleEndDate.isAfter(lastAttended, "day")) {
          return true;
        } else {
          return false;
        }
      } else {
        // If there is no attendance data or schedule end time, we consider the schedule as deletable
        return true;
      }
    },
    centerClassrooms() {
      if (this.teacherSchedule && this.teacherSchedule.centerId) {
        return this.classrooms.filter(
          (c) => c.centerId == this.teacherSchedule.centerId
        );
      } else {
        return this.classrooms;
      }
    },
  },
  async mounted() {
    this.initSchedule();

    this.initAttendance();
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    validTimeRange() {
      if (this.internalStartTime && this.internalEndTime) {
        return (
          this.internalEndTime > this.internalStartTime ||
          "End time cannot be earlier than start time."
        );
      }
      return true;
    },
    validateDaysOfWeek() {
      if (this.recurringEdit) {
        const atLeastOneDaySelected = this.daysOfWeek.some(
          (day) => day.selected
        );
        if (!atLeastOneDaySelected) {
          return "At least one day of the week must be selected for recurring schedules.";
        }
      }
      return true;
    },
    initSchedule() {
      this.internalStartTime = moment(this.teacherSchedule.startTime).format(
        "HH:mm"
      );
      this.internalEndTime = moment(this.teacherSchedule.endTime).format(
        "HH:mm"
      );
      this.selectedDate = moment(this.teacherSchedule.startTime).format(
        "YYYY-MM-DD"
      );
      this.selectedClassroom = this.teacherSchedule.classroomId;
      this.notes = this.teacherSchedule.notes;
      this.isRecurring = this.teacherSchedule.partOfRecurring;

      if (this.isRecurring) {
        this.daysOfWeek.forEach((dow) => {
          if (this.teacherSchedule.recurringDays.includes(dow.label)) {
            dow.selected = true;
          }
        });
      }
    },
    async initAttendance() {
      this.lastAttendedDate = await this.getLastAttendedDate(
        this.teacherSchedule.teacherId
      );
      this.isAttendanceDataLoaded = true;
    },
    async getLastAttendedDate(teacherId) {
      const params = {
        teacher_id: teacherId,
      };
      return await this.$axios
        .get("/teacher-schedule/get-last-timesheet", { params })
        .then((resp) => {
          const timesheet = resp.data;

          return timesheet ? timesheet.timeIn : null;
        })
        .catch((error) => {
          throw error;
        });
    },
    async updateSchedule() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // Custom validation for days of the week
      const daysOfWeekValid = this.validateDaysOfWeek();
      if (daysOfWeekValid !== true) {
        this.addErrorMessage(daysOfWeekValid);
        return;
      }

      this.isLoading = true;

      const teacherScheduleId = this.teacherSchedule.scheduleId;
      const originalDate = this.selectedDate;
      const newStartTime = moment(
        `${this.selectedDate}T${this.internalStartTime}`
      );
      const newEndTime = moment(`${this.selectedDate}T${this.internalEndTime}`);
      const newClassroomId = this.selectedClassroom;

      if (this.recurringEdit) {
        const showAttendanceAlert =
          this.isLastAttendedDateAfterRecurringStartDate();

        if (showAttendanceAlert) {
          if (
            window.confirm(
              `Attendance has been recorded for this recurring schedule. The last recorded attendance was on ${this.lastAttendedDate}. A new recurring schedule will be created after ${this.lastAttendedDate}. Proceed?`
            )
          ) {
            this.updateRecurringSchedule(newClassroomId);
          }
        } else {
          this.updateRecurringSchedule(newClassroomId);
        }
      } else {
        updateTeacherScheduleOccurrence(
          teacherScheduleId,
          originalDate,
          newStartTime,
          newEndTime,
          newClassroomId
        )
          .then((updatedScheduleInstance) => {
            this.isLoading = false;
            this.onClose();
            EventBus.$emit("reload-chart");
            EventBus.$emit("reload-dialog", this.parentID, {
              action: "updated",
              originalSchedule: this.teacherSchedule,
              schedule: updatedScheduleInstance,
            });
          })
          .catch((error) => {
            const schedule = {...this.teacherSchedule}
            schedule.startTime = newStartTime;
            schedule.endTime = newEndTime;
            schedule.classroomId = newClassroomId;
            this.handleErrorResponse(this, error, this.teacherSchedule);
          });
      }
    },
    onDeleteScheduleClick() {
      const schedule = this.teacherSchedule;

      if (this.recurringEdit) {
        const showAttendanceAlert =
          this.isLastAttendedDateAfterRecurringStartDate();

        if (showAttendanceAlert) {
          // Alert the user and get confirmation
          if (
            window.confirm(
              `Attendance has been recorded for this recurring schedule. The last recorded attendance was on ${this.lastAttendedDate}. Would you want to set the end date to the last attended date?`
            )
          ) {
            // We can set the end date to the last attended date.
            schedule.endTime = this.lastAttendedDate;
            this.deleteSchedule(schedule);
            this.addSuccessMessage(
              "Schedule end date set to the last attended date."
            );
          }
        } else {
          // no last attendance data found, can delete whole recurring schedule
          this.deleteSchedule(schedule);
        }
      } else {
        this.deleteSchedule(schedule);
      }
    },
    async deleteSchedule(schedule, emit = true) {
      const scheduleDate = moment(schedule.startTime).format("YYYY-MM-DD");

      // delete schedule if 'deleteRecurring' or a non-recurring schedule
      const params = {
        schedule_date:
          !schedule.partOfRecurring || this.recurringEdit ? null : scheduleDate,
      };
      await this.$axios
        .delete("/teacher-schedule/" + schedule.scheduleId, { params })
        .then(() => {
          // const event = {
          //   type: "deleted-teacher-schedule",
          //   data: schedule,
          //   teacherId: this.teacher ? this.teacher.id : null,
          // };

          if (emit) {
            this.onClose();
            EventBus.$emit("reload-chart");
            EventBus.$emit("reload-dialog", this.parentID, {
              action: "deleted",
              schedule,
            });
          }
        })
        .catch((error) => {
          if (emit) {
            this.$emit("on-error", error);
          }
          this.addErrorMessage(
            error.message || "Error deleting teacher schedule"
          );
        });
    },
    updateRecurringSchedule(newClassroomId) {
      const data = this.prepareNewScheduleData(this, newClassroomId);

      this.$axios
        .put(`/teacher-schedule/${this.teacherSchedule.scheduleId}`, data)
        .then((resp) => {
          this.handleSuccessResponse(this, resp.data.data);
        })
        .catch((error) => {
          this.handleErrorResponse(this, error, data);
        });
    },

    prepareNewScheduleData(context, newClassroomId) {
      return {
        centerId: context.teacherSchedule.centerId,
        teacherId: context.teacherSchedule.teacherId,
        classroomId: newClassroomId,
        startTime: context.internalStartTime,
        endTime: context.internalEndTime,
        recurring: true,
        startDate: context.selectedDate,
        endDate: context.endDate,
        notes: context.notes,
        daysOfWeek: context.daysOfWeek
          .filter((dow) => dow.selected)
          .map((dow) => dow.label),
        timezone: new Date().getTimezoneOffset(),
      };
    },
    handleSuccessResponse(context, savedSchedule) {
      context.addSuccessMessage("Teacher schedule updated");
      EventBus.$emit("reload-chart", { savedSchedule });
      context.onClose();
      context.isLoading = false;
    },

    handleErrorResponse(context, error, data) {
      context.isLoading = false;
      context.$emit("on-error", error);

      if (error.data && error.data.code === "OVERLAPPING_SCHEDULES") {
        const overlapSchedules = error.data.args[0];
        openOverlapScheduleErrors({
          schedule: data,
          teacher: context.selectedTeacher,
          overlappingSchedules: overlapSchedules,
        });
      }

      context.addErrorMessage(
        error.data.message || "Error creating new teacher schedule"
      );
    },
    toggleEditMode() {
      this.recurringEdit = !this.recurringEdit;
      const instanceStartDate = moment(this.teacherSchedule.startTime).format(
        "YYYY-MM-DD"
      );
      if (this.recurringEdit) {
        this.selectedDate = this.teacherSchedule.recurringStartDate
          ? this.teacherSchedule.recurringStartDate
          : instanceStartDate;
      } else {
        this.selectedDate = instanceStartDate;
      }
      this.endDate = this.teacherSchedule.recurringEndDate;
    },
    getStudentSchedules() {
      // Get student schedules corresponding to the teacher's schedules.

      let studentSchedules = [];
      if (this.teacherSchedule.teacherId) {
        studentSchedules = this.allItems.filter(
          (item) =>
            item.type == "student-schedule" &&
            item.teacherId == this.teacherSchedule.teacherId
        );
      } else if (this.teacherSchedule.classroomId) {
        studentSchedules = this.allItems.filter(
          (item) =>
            item.type == "student-schedule" &&
            item.classroomId == this.teacherSchedule.classroomId
        );
      }

      // filter out student schedule overlapping with teacher's schedule start and end time
      if (studentSchedules && studentSchedules.length > 0) {
        // Convert teacher's schedule to Moment objects
        const teacherStartTime = moment(
          this.teacherSchedule.startTime,
          "HH:mm"
        );
        const teacherEndTime = moment(this.teacherSchedule.endTime, "HH:mm");

        studentSchedules = studentSchedules.filter((ss) => {
          // Make copies of start and end so we don't modify the original objects
          let ssStart = ss.time.start.clone();
          let ssEnd = ss.time.end.clone();

          // Strip the date parts so we're left with today's date and the original time
          ssStart
            .year(teacherStartTime.year())
            .month(teacherStartTime.month())
            .date(teacherStartTime.date());
          ssEnd
            .year(teacherEndTime.year())
            .month(teacherEndTime.month())
            .date(teacherEndTime.date());

          // Now we can compare just the time parts
          return !(
            teacherStartTime.isBefore(ssEnd) && teacherEndTime.isAfter(ssStart)
          );
        });
      }

      return studentSchedules;
    },
    isLastAttendedDateAfterRecurringStartDate() {
      const lastAttendedDateMoment = this.lastAttendedDate
        ? moment(this.lastAttendedDate)
        : null;
      const recurringStartDateMoment = this.teacherSchedule.recurringStartDate
        ? moment(this.teacherSchedule.recurringStartDate)
        : null;

      return (
        lastAttendedDateMoment &&
        recurringStartDateMoment &&
        lastAttendedDateMoment.isAfter(recurringStartDateMoment)
      );
    },
  },
};
</script>
